import React, { useState, useRef } from 'react';
import SEO from '../components/SEO';
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PageBanner from '../components/PageBanner';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useCountries } from '../hooks/useCountries';

const Contact = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = translations[language].contact;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const { countries } = useCountries();

  const schema = yup.object().shape({
    title: yup.string().required("Title is required"),
    firstName: yup.string().required("First Name is required"),
    lastName: yup.string().required("Last Name is required"),
    phone: yup.string().matches(/^[0-9]+$/, "Phone must be digits").min(8, "Phone must be at least 8 digits").required("Phone number is required"),
    nationality: yup.string().required("Nationality is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    description: yup.string().required("Message is required"),
    privacy: yup.boolean().oneOf([true], "You must accept the privacy policy"),
  });

  const { register, handleSubmit, control, formState: { errors }, reset, setError, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '', // Changed default from 'Mr' to ''
      firstName: '',
      lastName: '',
      phone: '',
      nationality: '',
      email: '',
      description: '',
      countryCode: '+968', // Default backup
      privacy: false,
    }
  });

  const selectedNationality = watch("nationality");

  React.useEffect(() => {
    if (selectedNationality) {
      const country = countries.find(c => String(c.id) === String(selectedNationality));
      setValue("countryCode", (country && country.phone_prefix) || "+968");
    } else if (countries.length > 0) {
      // Set default to Oman if not selected
      const oman = countries.find(c => c.name.toLowerCase() === 'oman');
      if (oman) {
        setValue("nationality", String(oman.id));
        setValue("countryCode", oman.phone_prefix || "+968");
      }
    }
  }, [selectedNationality, countries, setValue]);

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const onSubmit = async (data) => {
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (recaptchaSiteKey && !captchaToken) {
      toast({
        title: language === 'ar' ? "خطأ في التحقق" : "Validation Error",
        description: language === 'ar' ? "يرجى إكمال التحقق من ReCAPTCHA." : "Please complete the ReCAPTCHA verification.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('firstName', data.firstName);
      formData.append('lastName', data.lastName);
      // Check if phone contains + or just digits. we combine them
      formData.append('phone', `${data.countryCode}${data.phone}`);
      formData.append('nationality', data.nationality);
      formData.append('email', data.email);
      formData.append('message', data.description || ""); // API expects 'message'
      formData.append('recaptcha_token', captchaToken);

      const response = await axios.post('https://admin.baicoman.com/api/contact-us', formData);

      if (response.status === 200 || response.status === 201) {
        toast({
          title: language === 'ar' ? "تم الإرسال بنجاح" : "Message Sent",
          description: language === 'ar' ? "شكراً لتواصلك معنا. سنعود إليك قريباً." : "Thank you for contacting us. We'll get back to you shortly.",
          className: "bg-green-600 text-white border-none"
        });
        reset();
        if (recaptchaSiteKey && recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
      }
    } catch (error) {
      console.error(error);
      if (error.response && error.response.data && error.response.data.errors) {
        const serverErrors = error.response.data.errors;
        Object.keys(serverErrors).forEach((field) => {
          setError(field, {
            type: "server",
            message: serverErrors[field][0],
          });
        });
        toast({
          title: language === 'ar' ? "خطأ في التحقق" : "Validation Error",
          description: language === 'ar' ? "يرجى التحقق من الحقول المطلوبة." : "Please check the form for errors.",
          variant: "destructive",
        });
      } else {
        toast({
          title: language === 'ar' ? "خطأ" : "Error",
          description: language === 'ar' ? "حدث خطأ ما. يرجى المحاولة مرة أخرى." : "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20 lg:pt-[120px]">
      <SEO
        title="Contact BAIC Oman | Get in Touch & Visit Our Showrooms"
        description="Contact BAIC Oman for inquiries, support, or a test drive. Call or visit us today."
        canonicalUrl="https://baicoman.com/connect/contact-us"
      />
      <h1 className="sr-only">Contact BAIC Oman</h1>
      {/* Hero Section */}
      <PageBanner
        image="/assets/images/call-center.png"
        imageAlt="Contact Us"
        contentClassName="items-end text-right"
      >
        <h2 className="text-5xl md:text-7xl font-bold text-white uppercase tracking-tight">{t.title}</h2>
      </PageBanner>

      {/* Info Cards Section */}


      {/* Form Section */}
      <div className="max-w-5xl mx-auto px-4 py-24">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-xl text-gray-800">{t.form.title}</h2>
          <p className="text-xl text-gray-500">
            {t.form.subtitle}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-4xl mx-auto bg-gray-50/50 p-8 rounded-3xl">
          {/* Title */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            <label className="text-gray-600 md:col-span-1">{t.form.fields.title}</label>
            <div className="md:col-span-3">
              <Controller
                control={control}
                name="title"
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="w-full bg-gray-100 border-none h-12 rounded-xl">
                      <SelectValue placeholder={t.form.placeholders.select} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mr">{t.form.fields.mr}</SelectItem>
                      <SelectItem value="Ms">{t.form.fields.ms}</SelectItem>
                      <SelectItem value="Mrs">{t.form.fields.mrs}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
            </div>
          </div>

          {/* First Name */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            <label className="text-gray-600 md:col-span-1">{t.form.fields.firstName}</label>
            <div className="md:col-span-3">
              <Input
                {...register("firstName")}
                className="bg-gray-100 border-none h-12 rounded-xl"
                placeholder={t.form.placeholders.firstName}
              />
              {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
            </div>
          </div>

          {/* Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            <label className="text-gray-600 md:col-span-1">{t.form.fields.lastName}</label>
            <div className="md:col-span-3">
              <Input
                {...register("lastName")}
                className="bg-gray-100 border-none h-12 rounded-xl"
                placeholder={t.form.placeholders.lastName}
              />
              {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
            </div>
          </div>

          {/* Phone Number */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            <label className="text-gray-600 md:col-span-1">{t.form.fields.phone}</label>
            <div className="md:col-span-3">
              <div className="flex gap-4">
                <span className="bg-gray-100 flex items-center px-4 rounded-xl text-gray-500 w-24 justify-center" dir="ltr">
                  {watch("countryCode") || "+968"}
                </span>
                <Input
                  {...register("phone")}
                  className="bg-gray-100 border-none h-12 rounded-xl flex-1"
                  placeholder={t.form.placeholders.phone}
                />
              </div>
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
            </div>
          </div>

          {/* Nationality */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            <label className="text-gray-600 md:col-span-1">{t.form.fields.nationality}</label>
            <div className="md:col-span-3">
              <Controller
                control={control}
                name="nationality"
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="w-full bg-gray-100 border-none h-12 rounded-xl">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country, index) => (
                        <SelectItem key={index} value={String(country.id)}>{country.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.nationality && <p className="text-red-500 text-xs mt-1">{errors.nationality.message}</p>}
            </div>
          </div>

          {/* Email */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            <label className="text-gray-600 md:col-span-1">{t.form.fields.email}</label>
            <div className="md:col-span-3">
              <Input
                {...register("email")}
                type="email"
                className="bg-gray-100 border-none h-12 rounded-xl"
                placeholder={t.form.placeholders.email}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>
          </div>

          {/* Description / Media Name */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
            <label className="text-gray-600 md:col-span-1 pt-3">{t.form.fields.mediaName}</label>
            <div className="md:col-span-3">
              <Textarea
                {...register("description")}
                className="bg-gray-100 border-none min-h-[150px] rounded-xl resize-none p-4 w-full"
                placeholder={t.form.placeholders.description}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description.message}</p>}
            </div>
          </div>

          <div className="md:col-span-4 pt-6 flex flex-col items-center gap-4">
            <div className="flex items-start md:items-center space-x-3 rtl:space-x-reverse max-w-2xl">
              {/* Custom Checkbox */}
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  id="privacy"
                  {...register("privacy")}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-gray-300 bg-white transition-all checked:border-red-600 checked:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500/20"
                />
                {/* Checkmark Icon */}
                <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-3.5 w-3.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
              </div>
              <label
                htmlFor="privacy"
                className="text-sm text-gray-600 cursor-pointer select-none leading-tight"
              >
                {t.form.fields.privacy}
              </label>
            </div>
            {errors.privacy && (
              <p className="text-red-500 text-xs">{errors.privacy.message}</p>
            )}

            {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
              <div className="w-full flex flex-col items-center gap-2">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                />
                {errors.recaptcha_token && <p className="text-red-500 text-xs">{errors.recaptcha_token.message}</p>}
              </div>
            )}
            <Button disabled={isSubmitting} type="submit" className="bg-red-600 hover:bg-red-700 text-white rounded-full px-12 py-6 text-sm uppercase tracking-widest font-medium w-full md:w-auto">
              {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit"}
            </Button>
          </div>
        </form>
      </div>

      {/* Map Section */}
      <div className="w-full h-[400px] bg-gray-200">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3608.667791694269!2d55.33408437609658!3d25.248037129623862!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d1720822607%3A0xe5a20668e16b939!2sAl%20Shaali%20Moto%20-%20BAIC%20Showroom!5e0!3m2!1sen!2sae!4v1709123456789!5m2!1sen!2sae"
          width="100%"
          height="100%"
          style={{ border: 0, filter: 'grayscale(0%)' }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </div>
  );
};

export default Contact;